import React, {useCallback, useMemo} from 'react'
import styled from 'styled-components'
import {
  getFontFamily,
  getFontSize,
  getInstagramLink,
  getWhatsAppLink,
} from 'utils'
import {useTranslation} from 'i18n'
import {IconType} from 'types'
import {useTheme} from 'themes'
import {IMAGE_ASSET} from 'consts'
import {Image, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import LegalTutorialContentItem from './LegalTutorialContentItem'

const StyledList = styled.ol`
  margin-left: ${convertUnit(0)};
  border: ${convertUnit(1)} solid;
  border-color: ${({theme}) => theme.gray_1};
  border-radius: ${convertUnit(8)};
  padding: 0 ${convertUnit(20)};
`

const StyledListSubItem = styled.div`
  padding-left: ${convertUnit(30)};
`
const StyledTextContainer = styled.div`
  text-align: justify;
  padding-bottom: ${convertUnit(20)};
`
const StyledTitle = styled(Paragraph)`
  ${({theme}) => ({
    color: theme.primary_5,
  })}
  text-align: justify;
  margin-top: 0;
`
const StyledSubTitle = styled(Paragraph)`
  ${({fontSize = 'xl', fontWeight = 'bold'}) => ({
    fontSize: typeof fontSize === 'number' ? fontSize : getFontSize(fontSize),
    fontFamily: getFontFamily(fontWeight),
  })}
  margin-top: ${convertUnit(20)};
  text-align: justify;
`
const StyledSettingContainer = styled.div`
  border: ${convertUnit(1)} solid;
  border-color: ${({theme}) => theme.gray_1};
  border-radius: ${convertUnit(8)};
  padding: ${convertUnit(20)};
  margin-bottom: ${convertUnit(20)};
`
const StyledLabelContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin-bottom: ${convertUnit(4)};
`
const StyledLink = styled.a`
  text-decoration: none;
`
const StyledImagesContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  height: ${convertUnit(20)};
  width: ${convertUnit(20)};
  padding: ${convertUnit(12)};
  border-radius: ${convertUnit(28)};
  background-color: ${({theme}) => theme.white_3};
  margin-right: ${convertUnit(12)};
  cursor: pointer;
`
const StyledImage = styled(Image)`
  width: ${convertUnit(20)};
  height: ${convertUnit(20)};
`

export default function LegalTutorialContent() {
  const {translate} = useTranslation()
  const theme = useTheme()
  const whatsappImg = IMAGE_ASSET('giftshop', 'whats-app.png')
  const instagramImg = IMAGE_ASSET('giftshop', 'instagram.png')
  const iconList = useMemo<ReadonlyArray<IconType>>(
    () => [
      'face-scan',
      'cart',
      'fg',
      'manage-content',
      'explore-ol',
      'bank',
      'tree-feature',
      'message',
      'user-lock',
      'robopet-alt',
    ],
    [],
  )

  const handleRenderTitle = useMemo(
    () => (
      <StyledTextContainer>
        <StyledTitle fontWeight="bold" fontSize="xxl">
          {translate('policy:tutorials')}
        </StyledTitle>
        <StyledSubTitle>{translate('policy:tutorialsDesc')}</StyledSubTitle>
      </StyledTextContainer>
    ),
    [translate],
  )

  const handleRenderSeparator = useMemo(
    () => (
      <div
        style={{
          height: convertUnit(1),
          backgroundColor: theme.gray_1,
        }}
      />
    ),
    [theme.gray_1],
  )

  const handleRenderLink = useMemo(
    () => (
      <>
        <StyledLink href={getWhatsAppLink()} target="_blank">
          <StyledImagesContainer>
            <StyledImage src={whatsappImg} alt="whats-app" />
          </StyledImagesContainer>
        </StyledLink>
        <StyledLink href={getInstagramLink()} target="_blank">
          <StyledImagesContainer>
            <StyledImage src={instagramImg} alt="instagram" />
          </StyledImagesContainer>
        </StyledLink>
      </>
    ),
    [instagramImg, whatsappImg],
  )

  const handleClickLink = useCallback((url: string) => {
    window.open(url, '_blank')
  }, [])

  const handleRenderItem = useMemo(
    () => (
      <>
        {Array.from({length: 10}).map((_, index) => (
          <StyledList key={index.toString()}>
            <LegalTutorialContentItem
              icon={iconList[index]}
              onClick={() => {
                if (index + 1 === 1) {
                  handleClickLink('https://fotoyu.com/link/1Jh1lWUuqsmQN0NsB')
                } else if (index + 1 === 8) {
                  handleClickLink('https://fotoyu.com/link/OvLoQcAe6jGkS0riN')
                } else if (index + 1 === 10) {
                  handleClickLink('https://fotoyu.com/link/9CCVVucNoKMpjGXzO')
                }
              }}
              text={translate('policy:tutorialsCategory', {
                context: `${index + 1}`,
              })}
              expandable={
                !(index + 1 === 1 || index + 1 === 8 || index + 1 === 10)
              }
              subItem={
                <>
                  {index + 1 === 2 && (
                    <StyledListSubItem>
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_2_A')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/9wacq3pcqind5A7r2',
                          )
                        }
                      />
                      {handleRenderSeparator}
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_2_B')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/k022EWUTLfG0ZXUTd',
                          )
                        }
                      />
                    </StyledListSubItem>
                  )}
                  {index + 1 === 3 && (
                    <StyledListSubItem>
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_3_A')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/CX9dsCWnjuvSyzFyf',
                          )
                        }
                      />
                      {handleRenderSeparator}
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_3_B')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/mFUNT4jrH8xHQqglN',
                          )
                        }
                      />
                      {handleRenderSeparator}
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_3_C')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/ZBm8tu0Il6qXteHd2',
                          )
                        }
                      />
                      {handleRenderSeparator}
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_3_D')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/4mzKVvePfUmjp1fJh',
                          )
                        }
                      />
                      {handleRenderSeparator}
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_3_E')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/Nxa9bHPKxS4SzUBpN',
                          )
                        }
                      />
                    </StyledListSubItem>
                  )}
                  {index + 1 === 4 && (
                    <StyledListSubItem>
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_4_A')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/JfUEPyNAg3tp7gvaV',
                          )
                        }
                      />
                      {handleRenderSeparator}
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_4_B')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/fMcn6SqSV9k8N4WXr',
                          )
                        }
                      />
                      {handleRenderSeparator}
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_4_C')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/q164ISzC9J6zbTzSA',
                          )
                        }
                      />
                      {handleRenderSeparator}
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_4_D')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/2AhuR5fmEckmK0oMZ',
                          )
                        }
                      />
                    </StyledListSubItem>
                  )}
                  {index + 1 === 5 && (
                    <StyledListSubItem>
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_5_A')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/6zs2MdVjjigpHJwrL',
                          )
                        }
                      />
                      {handleRenderSeparator}
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_5_B')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/bKCf73dHSOgpVLClk',
                          )
                        }
                      />
                      {handleRenderSeparator}
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_5_C')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/CLeIO6C3eQlmMiGBR',
                          )
                        }
                      />
                    </StyledListSubItem>
                  )}
                  {index + 1 === 6 && (
                    <StyledListSubItem>
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_6_A')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/X5GcSddM5STlKBlF6',
                          )
                        }
                      />
                      {handleRenderSeparator}
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_6_B')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/Af5FT4RqnqRyAj7iB',
                          )
                        }
                      />
                      {handleRenderSeparator}
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_6_C')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/TFnDnWhac8eRQ6o4W',
                          )
                        }
                      />
                    </StyledListSubItem>
                  )}
                  {index + 1 === 7 && (
                    <StyledListSubItem>
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_7_A')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/umTT5D0yVjcXgvJRT',
                          )
                        }
                      />
                      {handleRenderSeparator}
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_7_B')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/g1mwJSAD3yRfNpWuf',
                          )
                        }
                      />
                      {handleRenderSeparator}
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_7_C')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/jkfh0LPSeNGGhn7id',
                          )
                        }
                      />
                      {handleRenderSeparator}
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_7_D')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/kfxz5Gmpe5CFNoLat',
                          )
                        }
                      />
                    </StyledListSubItem>
                  )}
                  {index + 1 === 9 && (
                    <StyledListSubItem>
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_9_A')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/d1bSu6jqAJ2QlSNe8',
                          )
                        }
                      />
                      {handleRenderSeparator}
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_9_B')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/x6bfgOU4pE0ftySeM',
                          )
                        }
                      />
                      {handleRenderSeparator}
                      <LegalTutorialContentItem
                        text={translate('policy:tutorialsCategory_9_C')}
                        onClick={() =>
                          handleClickLink(
                            'https://fotoyu.com/link/i8YipWWMsvVs706ol',
                          )
                        }
                      />
                    </StyledListSubItem>
                  )}
                </>
              }
            />
          </StyledList>
        ))}
        <StyledSettingContainer>
          <StyledLabelContainer>
            <Paragraph fontSize="l" fontWeight="medium">
              {translate('giftShop:supportTitle')}
            </Paragraph>
          </StyledLabelContainer>
          <StyledLabelContainer>
            <Paragraph color="gray_4">
              {translate('policy:supportDesc')}
            </Paragraph>
          </StyledLabelContainer>
          <StyledLabelContainer>{handleRenderLink}</StyledLabelContainer>
        </StyledSettingContainer>
      </>
    ),
    [
      handleClickLink,
      handleRenderLink,
      handleRenderSeparator,
      iconList,
      translate,
    ],
  )

  return (
    <div id="tutorials">
      {handleRenderTitle}
      {handleRenderItem}
    </div>
  )
}
